import React, { useState } from 'react'
import { useMatchScreen } from '../../../context/MatchScreenContext'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { Article } from './articles'
import { ArticleCard } from './ArticleCard'
import { trackMatchScreenEvent } from './analytics'
import { nextIndex, prevIndex, visibleWindow } from './carousel'
import '../../styles/ArticleCarouselModule/ArticleCarousel.scss'

interface ArticleCarouselProps {
  articles: Article[]
}

// Below desktop width the carousel shows a single card; at or above it shows three.
const MOBILE_QUERY = '(max-width: 1024px)'
const DESKTOP_VISIBLE = 3
const MOBILE_VISIBLE = 1

const ChevronLeft = () => (
  <svg width="9" height="16" viewBox="0 0 9 16" fill="none" aria-hidden="true">
    <path d="M8 1 1 8l7 7" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ChevronRight = () => (
  <svg width="9" height="16" viewBox="0 0 9 16" fill="none" aria-hidden="true">
    <path d="m1 1 7 7-7 7" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export const ArticleCarousel = ({ articles }: ArticleCarouselProps) => {
  const { state: { pageKey } } = useMatchScreen()
  const isMobile = useMediaQuery(MOBILE_QUERY)
  const visibleCount = isMobile ? MOBILE_VISIBLE : DESKTOP_VISIBLE
  const [startIndex, setStartIndex] = useState(0)

  if (articles.length === 0) return null

  const visible = visibleWindow(articles, startIndex, visibleCount)

  const handleArticleClick = (article: Article) => {
    trackMatchScreenEvent('client-matches-article-click', {
      page_key: pageKey,
      title: article.title,
      url: article.url
    })
  }

  const handleNav = (direction: 'forward' | 'back') => {
    trackMatchScreenEvent('client-matches-article-carousel', { page_key: pageKey, direction })
    setStartIndex((current) =>
      direction === 'forward'
        ? nextIndex(current, articles.length)
        : prevIndex(current, articles.length)
    )
  }

  return (
    <div className="article-carousel" data-testid="article-carousel">
      <button
        type="button"
        className="article-carousel__nav article-carousel__nav--prev"
        aria-label="Previous articles"
        data-testid="article-carousel-prev"
        onClick={() => handleNav('back')}
      >
        <ChevronLeft />
      </button>
      <div className="article-carousel__track">
        {visible.map((article) => (
          <ArticleCard key={article.url} article={article} onSelect={handleArticleClick} />
        ))}
      </div>
      <button
        type="button"
        className="article-carousel__nav article-carousel__nav--next"
        aria-label="Next articles"
        data-testid="article-carousel-next"
        onClick={() => handleNav('forward')}
      >
        <ChevronRight />
      </button>
    </div>
  )
}
